// LayersComponent.jsx
// ============================================================================
// LAYERS + ROOMS PANEL (Schema v4)
// ----------------------------------------------------------------------------
// The Layers panel is the single home for two organizing surfaces:
//
//   • Layers — the existing non-destructive layer list (inpaint / style /
//              frame layers) with visibility, lock, rename, delete,
//              variations, and drag-to-reorder.
//
//   • Rooms  — Multi-angle organization for a renovation project.
//              Each room is a named background (its own canvasImage) so a
//              user can hold several angles / facets of the same space
//              (e.g. gallery wall side vs. entertainment-center side) as
//              sibling rooms and switch between them instantly.
//
//              Any room can be promoted to be the project's PRIMARY room
//              by tapping its number badge. Promoting moves that room to
//              the top of the room list and swaps its canvas in as the
//              editor's base image — handy when the angle you care about
//              most changes mid-project.
//
// The panel is tabbed. Tabs can be driven externally (controlled) so the
// Editor's top nav bar can open the panel directly on the Rooms tab.
//
// A project ALWAYS has at least one room. The FIRST room in the list is
// the "Primary Canvas" — the project's primary edit origin. It carries a
// permanent descriptive sublabel ("Your primary edit origin layer for
// <project name>.") so users understand it's the foundation the rest of
// the project builds on. This component additionally guarantees a floor
// of 1 by hiding the delete control when only one room exists, and it
// hides both the delete and the rename controls on whichever room
// currently holds the primary slot.
//
// ----------------------------------------------------------------------------
// VISUAL SYSTEM — ROOMS (v6)
// ----------------------------------------------------------------------------
//   • The room number lives as a SOLID blue circle badge in the
//     top-left corner of the thumbnail. Solid #4594f9 background,
//     solid #ffffff text, thin white ring for legibility over busy
//     thumbnails. Never translucent, never gradient, never outlined.
//     Primary is static; other rooms' badges are tappable promote
//     buttons.
//   • No glows, no drop shadows, no text-shadows on any room surface.
//   • Selection is communicated by a subtle background shift:
//       – Dark mode:  #4a4a4a (lighter than the default #3a3a3a)
//       – Light mode: #e8e8e8 (darker than the default #f5f5f5)
//     plus a 1.5px border and a 2px soft-gray thumbnail border.
//   • The active badge is a solid blue circle with a white check and a
//     white 2px ring — no shadow. It sits in the BOTTOM-RIGHT corner of
//     the thumbnail so it never collides with the number badge.
//   • The primary room's sublabel is gray, with the project name in
//     bold so the user can spot it at a glance.
// ============================================================================

import React, { useState, useRef } from 'react';
import {
  FiEye,
  FiEyeOff,
  FiLock,
  FiUnlock,
  FiEdit2,
  FiImage,
  FiTrash2,
  FiPlus,
  FiCheck,
  FiUpload,
} from 'react-icons/fi';
import { LuPenTool } from 'react-icons/lu';
import { RiPaintingLine } from 'react-icons/ri';
import { TbCubePlus } from 'react-icons/tb';
import { getStorage, ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';

// ============================================================================
// HEIC / HEIF CONVERSION
// ----------------------------------------------------------------------------
// iPhones default to HEIC since iOS 11. No browser except Safari 17+ can
// decode HEIC via <img>, createImageBitmap(), or canvas — so every consumer
// upload surface breaks on a raw iPhone photo in Chrome, Firefox, and Edge.
// heic2any is the standard client-side converter: it decodes the HEIC
// container and re-encodes it as a JPEG/PNG Blob the browser can render.
// ============================================================================
const convertHeicToJpeg = async (file) => {
  const isDng = /\.dng$/i.test(file.name) || file.type === 'image/dng' || file.type === 'image/x-adobe-dng';
  if (isDng) {
    throw new Error('DNG (camera raw) files are not supported yet. Please export your photo as JPEG or HEIC before uploading.');
  }
  const isHeic = /\.(heic|heif)$/i.test(file.name) ||
    file.type === 'image/heic' ||
    file.type === 'image/heif';
  if (!isHeic) return file;

  try {
    const heic2any = (await import('heic2any')).default;
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
    // heic2any returns a Blob (or an array of Blobs for multi-image HEIC).
    // We take the first frame — that's the primary photo.
    const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch (err) {
    console.error('[HEIC] conversion failed:', err);
    throw new Error('Could not read this HEIC photo. Try exporting it as JPEG first.');
  }
};

// ============================================================================
// ROOM IMAGE DOWNSCALE
// ----------------------------------------------------------------------------
// Room backgrounds can be large phone photos (12 MP+). A room holds its own
// full-resolution canvas base, and switching rooms swaps that base into the
// editor. Downscaling once at upload time caps the per-room memory and
// storage cost with no perceptible quality loss for a renovation reference
// image that will be displayed at canvas resolution.
// ============================================================================
const downscaleRoomImage = (dataUrl, maxEdge = 2048) => {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:')) {
      resolve({ dataUrl, width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const longEdge = Math.max(w, h);
      if (!longEdge || longEdge <= maxEdge) {
        resolve({ dataUrl, width: w, height: h });
        return;
      }
      const ratio = maxEdge / longEdge;
      const newW = Math.round(w * ratio);
      const newH = Math.round(h * ratio);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = newW;
        canvas.height = newH;
        const ctx = canvas.getContext('2d', { colorSpace: 'display-p3' });
        ctx.drawImage(img, 0, 0, newW, newH);
        let hasAlpha = false;
        try {
          const sample = ctx.getImageData(0, 0, 1, 1).data;
          hasAlpha = sample[3] < 255;
        } catch (e) {
          hasAlpha = false;
        }
        const out = hasAlpha
          ? canvas.toDataURL('image/png')
          : canvas.toDataURL('image/jpeg', 0.92);
        resolve({ dataUrl: out, width: newW, height: newH });
      } catch (err) {
        resolve({ dataUrl, width: w, height: h });
      }
    };
    img.onerror = () => resolve({ dataUrl, width: 0, height: 0 });
    img.src = dataUrl;
  });
};

const LayersComponent = ({
  // ─── Layers (existing) ────────────────────────────────────────────────────
  layers,
  selectedLayerId,
  onSelectLayer,
  onRenameLayer,
  onDeleteLayer,
  onShowVariations,
  onSelectVariation,
  onToggleVisibility,
  onToggleLock,
  onReorderLayers,

  // ─── Rooms ────────────────────────────────────────────────────────────────
  rooms = [],
  activeRoomId = null,
  onSelectRoom,
  onAddRoom,
  onRenameRoom,
  onDeleteRoom,
  onSetPrimaryRoom,
  projectName = '',

  // ─── Persistence context (used for immediate Storage upload of room
  //     artwork, mirroring PictureFrameTool's pre-project staging path) ────
  authUser = null,
  projectId = null,

  // ─── Tab control (optional) ───────────────────────────────────────────────
  activeTab: controlledActiveTab,
  onTabChange,
  defaultTab = 'layers',
}) => {
  // ─── Tab state (controlled or uncontrolled) ───────────────────────────────
  const [internalTab, setInternalTab] = useState(defaultTab);
  const activeTab =
    controlledActiveTab !== undefined ? controlledActiveTab : internalTab;
  const setActiveTab = (tab) => {
    if (onTabChange) onTabChange(tab);
    if (controlledActiveTab === undefined) setInternalTab(tab);
  };

  // ─── Existing layer edit / drag state ─────────────────────────────────────
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [editName, setEditName] = useState('');
  const [draggedLayerIndex, setDraggedLayerIndex] = useState(null);
  const [dragOverLayerIndex, setDragOverLayerIndex] = useState(null);

  // ─── Rooms state ──────────────────────────────────────────────────────────
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editRoomName, setEditRoomName] = useState('');
  const [isUploadingRoom, setIsUploadingRoom] = useState(false);
  const roomFileInputRef = useRef(null);

  // Primary room sublabel — names the project when we know it.
  const trimmedProjectName =
    projectName && String(projectName).trim() ? String(projectName).trim() : '';

  // ============================================================================
  // EXISTING LAYER HANDLERS (unchanged)
  // ============================================================================
  const handleStartRename = (layer, e) => {
    e.stopPropagation();
    setEditingLayerId(layer.id);
    setEditName(layer.name);
  };

  const handleSaveRename = (layerId) => {
    if (editName.trim()) {
      onRenameLayer(layerId, editName.trim());
    }
    setEditingLayerId(null);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedLayerIndex(index);
    const dragImg = new Image();
    dragImg.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    e.dataTransfer.setDragImage(dragImg, 0, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLayerIndex(index);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      draggedLayerIndex !== null &&
      draggedLayerIndex !== targetIndex &&
      onReorderLayers
    ) {
      onReorderLayers(draggedLayerIndex, targetIndex);
    }
    setDraggedLayerIndex(null);
    setDragOverLayerIndex(null);
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    setDraggedLayerIndex(null);
    setDragOverLayerIndex(null);
  };

  // ============================================================================
  // ROOMS HANDLERS
  // ============================================================================
  const handleAddRoomClick = () => {
    if (isUploadingRoom) return;
    if (roomFileInputRef.current) roomFileInputRef.current.click();
  };

  const handleRoomFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setIsUploadingRoom(true);

    for (const file of files) {
      try {
        // Convert HEIC/HEIF to JPEG before anything else touches the file.
        // Without this step the FileReader produces a data URL the browser
        // cannot render, and the room thumbnail + canvas both go gray.
        const displayFile = await convertHeicToJpeg(file);

        const rawDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(displayFile);
        });

        const { dataUrl, width, height } = await downscaleRoomImage(rawDataUrl);

        const newRoomId = `room_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        // ════════════════════════════════════════════════════════════════
        // IMMEDIATE UPLOAD TO STORAGE
        // -----------------------------------------------------------------
        // Mirrors PictureFrameTool's behaviour: upload the artwork to
        // Storage THE MOMENT the user picks it, and store the returned
        // download URL on the room record. Without this, the room's
        // artwork lives only as an in-memory data URL until the project
        // auto-save fires — and if the save never fires (user closes the
        // tab within the 30 s auto-save interval, network drop, etc.)
        // the image is gone forever.
        //
        // Storage rules already cover both target paths via
        // `users/{uid}/{allPaths=**}`:
        //   users/{uid}/projects/{pid}/rooms/{rid}/canvas.jpg  (in-project)
        //   users/{uid}/rooms_staging/{rid}/canvas.jpg         (pre-project)
        // The pre-project path is used for brand-new projects that don't
        // yet have a Firestore id; the URL is durable either way, and
        // the save pipeline skips re-uploading non-data URLs.
        // ════════════════════════════════════════════════════════════════
        let storedUrl = dataUrl;
        if (authUser) {
          try {
            const storage = getStorage();
            const path = projectId
              ? `users/${authUser.uid}/projects/${projectId}/rooms/${newRoomId}/canvas.jpg`
              : `users/${authUser.uid}/rooms_staging/${newRoomId}/canvas.jpg`;
            const roomImgRef = storageRef(storage, path);
            const mime = (dataUrl.match(/^data:([^;]+)/) || [])[1] || 'image/jpeg';
            await uploadString(roomImgRef, dataUrl, 'data_url', { contentType: mime });
            storedUrl = await getDownloadURL(roomImgRef);
          } catch (uploadErr) {
            console.error('[Rooms] canvas upload failed, falling back to data URL:', uploadErr);
            storedUrl = dataUrl;
          }
        }

        const newRoom = {
          id: newRoomId,
          name: (file.name || 'New Room').replace(/\.[^/.]+$/, '') || 'New Room',
          canvasImage: storedUrl,
          thumbnail: storedUrl,
          naturalWidth: width,
          naturalHeight: height,
          layerCount: 0,
          createdAt: Date.now(),
        };

        if (onAddRoom) onAddRoom(newRoom);
      } catch (err) {
        console.error('[Rooms] upload failed:', err);
      }
    }

    setIsUploadingRoom(false);
    e.target.value = '';
  };

  const handleStartRenameRoom = (room, e) => {
    e.stopPropagation();
    setEditingRoomId(room.id);
    setEditRoomName(room.name);
  };

  const handleSaveRenameRoom = (roomId) => {
    if (editRoomName.trim() && onRenameRoom) {
      onRenameRoom(roomId, editRoomName.trim());
    }
    setEditingRoomId(null);
  };

  const handleSelectRoom = (room) => {
    if (!room) return;
    if (room.id === activeRoomId) return;
    if (onSelectRoom) onSelectRoom(room.id, room);
  };

  const handleDeleteRoomClick = (room, e) => {
    e.stopPropagation();
    if (rooms.length <= 1) return;
    if (onDeleteRoom) onDeleteRoom(room.id);
  };

  // ── Promote a room to the primary slot ────────────────────────────────────
  const handleSetPrimaryRoomClick = (room, e) => {
    e.stopPropagation();
    if (!room) return;
    if (rooms[0] && rooms[0].id === room.id) return; // already primary
    if (onSetPrimaryRoom) onSetPrimaryRoom(room.id, room);
  };

  // ============================================================================
  // LAYER PARTITIONING (unchanged)
  // ============================================================================
  const quickStyleLayers = layers.filter((l) => l.type === 'style');
  const regularLayers = layers.filter((l) => l.type !== 'style');
  const isEmpty = layers.length === 0;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="layers-panel">
      {/* ═══════════════════════════════════════════════════════════════════
          TAB BAR
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="layers-tab-bar" role="tablist" aria-label="Layers and Rooms">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'layers'}
          className={`layers-tab ${activeTab === 'layers' ? 'active' : ''}`}
          onClick={() => setActiveTab('layers')}
        >
          <LuPenTool size={12} />
          <span className="layers-tab-label">Layers</span>
          {layers.length > 0 && (
            <span className="layers-tab-count">{layers.length}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'rooms'}
          className={`layers-tab ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => setActiveTab('rooms')}
        >
          <TbCubePlus size={12} />
          <span className="layers-tab-label">Rooms</span>
          {rooms.length > 0 && (
            <span className="layers-tab-count">{rooms.length}</span>
          )}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TAB CONTENT
          ═══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'layers' ? (
        // ──────────────────────────────────────────────────────────────────
        // LAYERS TAB — existing content preserved exactly
        // ──────────────────────────────────────────────────────────────────
        isEmpty ? (
          <div className="layers-empty">
            No layers yet. Use Pen Lasso to create a shape.
          </div>
        ) : (
          <>
            <div className="layers-top-section">
              {regularLayers.length === 0 && quickStyleLayers.length === 0 ? (
                <div className="layers-empty">
                  No layers yet. Use Pen Lasso to create a shape.
                </div>
              ) : (
                regularLayers.map((layer, index) => (
                  <div
                    key={layer.id}
                    className={`layer-item ${
                      selectedLayerId === layer.id ? 'selected' : ''
                    } ${dragOverLayerIndex === index ? 'drag-over' : ''} ${
                      layer.locked ? 'locked' : ''
                    }`}
                    onClick={() => !layer.locked && onSelectLayer(layer.id)}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="layer-top-row">
                      <div
                        className="layer-visibility"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleVisibility(layer.id);
                        }}
                      >
                        {layer.visible !== false ? (
                          <FiEye size={14} />
                        ) : (
                          <FiEyeOff size={14} />
                        )}
                      </div>

                      <div
                        className={`layer-source-icon ${
                          layer.type === 'frame' ? 'source-frame' : 'source-pen'
                        }`}
                        title={
                          layer.type === 'frame'
                            ? 'Picture Frame'
                            : 'Pen Lasso / Inpainting'
                        }
                      >
                        {layer.type === 'frame' ? (
                          <RiPaintingLine size={12} />
                        ) : (
                          <LuPenTool size={12} />
                        )}
                      </div>

                      <div className="layer-info">
                        {editingLayerId === layer.id ? (
                          <input
                            type="text"
                            className="layer-rename-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => handleSaveRename(layer.id)}
                            onKeyPress={(e) =>
                              e.key === 'Enter' && handleSaveRename(layer.id)
                            }
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="layer-name">{layer.name}</span>
                        )}
                      </div>
                    </div>

                    <div className="layer-actions">
                      <button
                        className="layer-action-btn lock-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleLock(layer.id);
                        }}
                        title={layer.locked ? 'Unlock' : 'Lock'}
                      >
                        {layer.locked ? (
                          <FiLock size={12} />
                        ) : (
                          <FiUnlock size={12} />
                        )}
                      </button>
                      <button
                        className="layer-action-btn rename-btn"
                        onClick={(e) => handleStartRename(layer, e)}
                        title="Rename"
                      >
                        <FiEdit2 size={12} />
                      </button>
                      <button
                        className={`layer-action-btn variations-btn ${
                          !layer.variations || layer.variations.length === 0
                            ? 'disabled'
                            : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (layer.variations && layer.variations.length > 0) {
                            onShowVariations(layer.id);
                          }
                        }}
                        title="Variations"
                        disabled={!layer.variations || layer.variations.length === 0}
                      >
                        <FiImage size={12} />
                      </button>
                      <button
                        className="layer-action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteLayer(layer.id);
                        }}
                        title="Delete"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {(quickStyleLayers.length > 0 || selectedLayerId) && (
              <div className="layers-bottom-section">
                {quickStyleLayers.length > 0 && (
                  <>
                    <div className="layers-subsection-header">
                      Quick Style Layers
                    </div>
                    {quickStyleLayers.map((layer, index) => (
                      <div
                        key={layer.id}
                        className={`layer-item ${
                          selectedLayerId === layer.id ? 'selected' : ''
                        } ${
                          dragOverLayerIndex === index + regularLayers.length
                            ? 'drag-over'
                            : ''
                        } ${layer.locked ? 'locked' : ''}`}
                        onClick={() => !layer.locked && onSelectLayer(layer.id)}
                        draggable={true}
                        onDragStart={(e) =>
                          handleDragStart(e, index + regularLayers.length)
                        }
                        onDragOver={(e) =>
                          handleDragOver(e, index + regularLayers.length)
                        }
                        onDrop={(e) =>
                          handleDrop(e, index + regularLayers.length)
                        }
                        onDragEnd={handleDragEnd}
                      >
                        <div className="layer-top-row">
                          <div
                            className="layer-visibility"
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleVisibility(layer.id);
                            }}
                          >
                            {layer.visible !== false ? (
                              <FiEye size={14} />
                            ) : (
                              <FiEyeOff size={14} />
                            )}
                          </div>

                          <div
                            className={`layer-source-icon ${
                              layer.type === 'frame'
                                ? 'source-frame'
                                : 'source-pen'
                            }`}
                            title={
                              layer.type === 'frame'
                                ? 'Picture Frame'
                                : 'Pen Lasso / Inpainting'
                            }
                          >
                            {layer.type === 'frame' ? (
                              <RiPaintingLine size={12} />
                            ) : (
                              <LuPenTool size={12} />
                            )}
                          </div>

                          <div className="layer-info">
                            {editingLayerId === layer.id ? (
                              <input
                                type="text"
                                className="layer-rename-input"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onBlur={() => handleSaveRename(layer.id)}
                                onKeyPress={(e) =>
                                  e.key === 'Enter' &&
                                  handleSaveRename(layer.id)
                                }
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span className="layer-name">{layer.name}</span>
                            )}
                          </div>
                        </div>

                        <div className="layer-actions">
                          <button
                            className="layer-action-btn lock-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleLock(layer.id);
                            }}
                            title={layer.locked ? 'Unlock' : 'Lock'}
                          >
                            {layer.locked ? (
                              <FiLock size={12} />
                            ) : (
                              <FiUnlock size={12} />
                            )}
                          </button>
                          <button
                            className="layer-action-btn rename-btn"
                            onClick={(e) => handleStartRename(layer, e)}
                            title="Rename"
                          >
                            <FiEdit2 size={12} />
                          </button>
                          <button
                            className={`layer-action-btn variations-btn ${
                              !layer.variations ||
                              layer.variations.length === 0
                                ? 'disabled'
                                : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                layer.variations &&
                                layer.variations.length > 0
                              ) {
                                onShowVariations(layer.id);
                              }
                            }}
                            title="Variations"
                            disabled={
                              !layer.variations ||
                              layer.variations.length === 0
                            }
                          >
                            <FiImage size={12} />
                          </button>
                          <button
                            className="layer-action-btn delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteLayer(layer.id);
                            }}
                            title="Delete"
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {selectedLayerId && (
                  <div className="layers-variations-section">
                    <div className="layers-subsection-header">Variations</div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap',
                        padding: '0 16px 12px 16px',
                      }}
                    >
                      {(
                        layers.find((l) => l.id === selectedLayerId)
                          ?.variations || []
                      )
                        .slice(0, 4)
                        .map((variation, index) => (
                          <div
                            key={index}
                            onClick={() =>
                              onSelectVariation &&
                              onSelectVariation(variation, index)
                            }
                            style={{
                              width: '56px',
                              height: '56px',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: '2px solid transparent',
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.borderColor = '#4594f9')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.borderColor = 'transparent')
                            }
                          >
                            <img
                              src={variation}
                              alt={`Var ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )
      ) : (
        // ──────────────────────────────────────────────────────────────────
        // ROOMS TAB
        //
        // Design notes:
        //   • Same conceptual primitives as the Picture Frame tool: an
        //     upload affordance at the top, a scrollable collection below,
        //     and per-item action buttons.
        //   • Different structural choice: a LIST of horizontal room cards
        //     (thumbnail + name + meta + actions) rather than a grid of
        //     square thumbnails.
        //   • The FIRST room in the list is the "Primary Canvas". It carries
        //     a small descriptive sublabel under its name and cannot be
        //     deleted or renamed. Any other room can be promoted into that
        //     slot by tapping its number badge on the thumbnail.
        //   • Visual: number badge is a solid blue circle on the top-left
        //     of the thumbnail. Active state = subtle lighter/darker gray
        //     card background + 1.5px border + 2px soft-gray thumbnail
        //     border. No glows, no drop shadows anywhere.
        // ──────────────────────────────────────────────────────────────────
        <div className="rooms-panel">
          {/* Add Room affordance */}
          <div className="rooms-add-section">
            <button
              type="button"
              className="rooms-add-btn"
              onClick={handleAddRoomClick}
              disabled={isUploadingRoom}
            >
              {isUploadingRoom ? (
                <>
                  <span className="rooms-add-spinner" />
                  <span>Adding…</span>
                </>
              ) : (
                <>
                  <FiUpload size={14} />
                  <span>Add Room</span>
                </>
              )}
            </button>
            <input
              ref={roomFileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleRoomFileUpload}
              style={{ display: 'none' }}
            />
            <p className="rooms-add-hint">
              Upload additional Interior Layouts or Separate Rooms.
            </p>
          </div>

          {/* Room list */}
          {rooms.length === 0 ? (
            <div className="rooms-empty">
              <TbCubePlus size={28} />
              <p className="rooms-empty-title">No rooms yet</p>
              <p className="rooms-empty-hint">
                Add a room to organize angles, layouts, and zones of your
                renovation.
              </p>
            </div>
          ) : (
            <div className="rooms-list">
              {rooms.map((room, index) => {
                const isActive = activeRoomId === room.id;
                const isEditing = editingRoomId === room.id;
                const isPrimaryRoom = index === 0;
                const displayName = isPrimaryRoom ? 'Primary Canvas' : room.name;
                const roomNumber = index + 1;

                // Layer count — always visible, on every row, in every
                // state (Active or "Tap to Toggle").
                //   • For the ACTIVE room, the editor's `layers` prop IS
                //     that room's layers, so `layers.length` is the live,
                //     authoritative count.
                //   • For INACTIVE rooms, fall back to the count stamped
                //     on the room record by the save pipeline
                //     (room.layerCount). If that field is missing — e.g.
                //     legacy rooms saved before the field existed — we
                //     show 0 rather than hiding the line, so the row
                //     never collapses and users always see the slot.
                const effectiveLayerCount = isActive
                  ? (Array.isArray(layers) ? layers.length : 0)
                  : (typeof room.layerCount === 'number' ? room.layerCount : 0);

                return (
                  <div
                    key={room.id}
                    className={`room-item ${isActive ? 'active' : ''} ${
                      isEditing ? 'editing' : ''
                    } ${isPrimaryRoom ? 'primary-room' : ''}`}
                    onClick={() => handleSelectRoom(room)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (isEditing) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectRoom(room);
                      }
                    }}
                  >
                    {/* Thumbnail with number badge (top-left) and, when
                        active, a checkmark badge (bottom-right). */}
                    <div className="room-thumbnail">
                      {(room.thumbnail || room.canvasImage) ? (
                        <img
                          src={room.thumbnail || room.canvasImage}
                          alt={displayName}
                          draggable={false}
                        />
                      ) : (
                        <div className="room-thumbnail-fallback">
                          <TbCubePlus size={24} />
                        </div>
                      )}

                      {/* Number badge — solid blue circle, solid white
                          number, thin white ring so it stays legible on
                          any thumbnail. Primary is static; other rooms'
                          badges are tappable promote buttons. */}
                      <span
                        className={`room-thumbnail-number ${
                          isPrimaryRoom
                            ? 'room-thumbnail-number-primary'
                            : 'room-thumbnail-number-button'
                        }`}
                        onClick={(e) =>
                          !isPrimaryRoom && handleSetPrimaryRoomClick(room, e)
                        }
                        role={isPrimaryRoom ? undefined : 'button'}
                        tabIndex={isPrimaryRoom ? -1 : 0}
                        onKeyDown={(e) => {
                          if (isPrimaryRoom) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSetPrimaryRoomClick(room, e);
                          }
                        }}
                        title={
                          isPrimaryRoom
                            ? 'Primary Canvas'
                            : `Make ${displayName} the primary canvas`
                        }
                        aria-label={
                          isPrimaryRoom
                            ? 'Primary Canvas'
                            : `Make ${displayName} the primary canvas`
                        }
                      >
                        {roomNumber}
                      </span>

                    </div>

                    {/* Info */}
                    <div className="room-info">
                      <div className="room-title-row">
                        {isEditing ? (
                          <input
                            type="text"
                            className="room-rename-input"
                            value={editRoomName}
                            onChange={(e) => setEditRoomName(e.target.value)}
                            onBlur={() => handleSaveRenameRoom(room.id)}
                            onKeyPress={(e) =>
                              e.key === 'Enter' && handleSaveRenameRoom(room.id)
                            }
                            onKeyDown={(e) => e.stopPropagation()}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="room-name">{displayName}</span>
                        )}
                      </div>
                      {isPrimaryRoom && (
                        <span className="room-sublabel">
                          Your project's source layer for{' '}
                          {trimmedProjectName ? (
                            <strong className="room-sublabel-project">
                              {trimmedProjectName}
                            </strong>
                          ) : (
                            'this project'
                          )}
                          .
                        </span>
                      )}
                      <span className="room-meta">
                        <span className="room-layer-count">
                          {effectiveLayerCount} layer
                          {effectiveLayerCount === 1 ? '' : 's'}
                        </span>
                        {isActive ? (
                          <span className="room-meta-active">Active</span>
                        ) : (
                          <span className="room-meta-toggle-hint">
                            Tap to Toggle
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="room-actions">
                      {!isPrimaryRoom && (
                        <button
                          type="button"
                          className="room-action-btn room-rename-btn"
                          onClick={(e) => handleStartRenameRoom(room, e)}
                          title="Rename"
                          aria-label={`Rename ${displayName}`}
                        >
                          <FiEdit2 size={12} />
                        </button>
                      )}
                      {rooms.length > 1 && !isPrimaryRoom && (
                        <button
                          type="button"
                          className="room-action-btn room-delete-btn"
                          onClick={(e) => handleDeleteRoomClick(room, e)}
                          title="Delete room"
                          aria-label={`Delete ${displayName}`}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          STYLES
          ═══════════════════════════════════════════════════════════════════ */}
      <style>{`
        .layers-panel {
          display: flex;
          flex-direction: column;
          gap: 0;
          transition: all 0.2s ease;
          overflow: hidden;
          height: 100%;
          min-height: 0;
          width: 100%;
        }

        /* ============================================
           TAB BAR
           ============================================ */
        .layers-tab-bar {
          display: flex;
          flex-shrink: 0;
          gap: 0;
          border-bottom: 1px solid #444;
          background: #2f2f2f;
          position: relative;
          z-index: 2;
        }
        .theme-light .layers-tab-bar {
          background: #fafafa;
          border-bottom-color: #e0e0e0;
        }

        .layers-tab {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 10px;
          border: none;
          background: transparent;
          color: #888;
          font-family: Avenir, sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          cursor: pointer;
          transition: color 0.2s ease;
          position: relative;
          outline: none;
          -webkit-tap-highlight-color: transparent;
        }
        .theme-light .layers-tab {
          color: #888;
        }
        .theme-dark .layers-tab {
          color: #888;
        }
        .layers-tab:hover:not(.active) {
          color: #bbb;
        }
        .theme-light .layers-tab:hover:not(.active) {
          color: #555;
        }
        .layers-tab.active {
          color: #4594f9;
        }
        .layers-tab.active::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -1px;
          height: 2px;
          background: #4594f9;
          border-radius: 2px 2px 0 0;
        }
        .layers-tab-label {
          display: inline-block;
        }
        .layers-tab-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 16px;
          padding: 0 5px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.08);
          color: #aaa;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0;
        }
        .theme-light .layers-tab-count {
          background: rgba(0, 0, 0, 0.06);
          color: #888;
        }
        .layers-tab.active .layers-tab-count {
          background: rgba(69, 148, 249, 0.18);
          color: #4594f9;
        }

        /* ============================================
           LAYERS TAB CONTENT (unchanged structure)
           ============================================ */
        .layers-top-section {
          flex: 1 1 auto;
          overflow-y: auto;
          min-height: 0;
        }

        .layers-bottom-section {
          flex: 0 0 auto;
          margin-top: auto;
          border-top: 1px solid #555;
        }
        .theme-light .layers-bottom-section {
          border-top-color: #e0e0e0;
        }

        .layers-empty {
          display: block;
          text-align: center;
          padding: 40px 24px;
          font-size: 12px;
          font-family: Avenir, sans-serif;
          box-sizing: border-box;
          width: 100%;
          line-height: 1.5;
          margin: 0 auto;
        }
        .theme-light .layers-empty { color: #666; }
        .theme-dark .layers-empty { color: #999; }
        .layers-empty { color: #999; }

        .layers-subsection-header {
          font-family: 'Avenir', sans-serif;
          font-weight: 600;
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 12px 16px 8px 16px;
          border-bottom: 1px solid #333;
        }
        .theme-light .layers-subsection-header {
          color: #888;
          border-top-color: #e0e0e0;
          border-bottom-color: #f0f0f0;
        }
        .theme-dark .layers-subsection-header {
          color: #aaa;
          border-top-color: #555;
          border-bottom-color: #333;
        }

        .layer-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px 16px;
          background-color: #3a3a3a;
          border-radius: 0;
          cursor: grab;
          border: none;
          border-bottom: 1px solid #444;
          transition: background-color 0.2s ease, border-color 0.2s ease;
          margin: 0;
          width: 100%;
          box-sizing: border-box;
        }

        .layer-top-row {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }
        .layer-item:first-child {
          border-top: 1px solid #444;
        }
        .theme-light .layer-item {
          background-color: #f5f5f5;
          border-bottom-color: #e0e0e0;
        }
        .theme-light .layer-item:first-child {
          border-top-color: #e0e0e0;
        }
        .layer-item.drag-over { border-top: 2px solid #4594f9; }
        .layer-item.locked { opacity: 0.7; cursor: not-allowed; }
        .theme-light .layer-item { background-color: #f5f5f5; }
        .layer-item:hover { background-color: #444; }
        .theme-light .layer-item:hover { background-color: #e8e8e8; }
        .layer-item.selected {
          border-left: 3px solid #4594f9;
          background-color: #3a3a5a;
        }
        .theme-light .layer-item.selected {
          background-color: #e8f0ff;
          border-left: 3px solid #4594f9;
        }
        .layer-visibility {
          cursor: pointer;
          color: #888;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .layer-visibility:hover { color: #4594f9; background: rgba(69, 148, 249, 0.2); }
        .layer-info {
          flex: 1;
          min-width: 0;
        }
        .layer-source-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          flex-shrink: 0;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .layer-source-icon.source-frame {
          color: #4594f9;
          background: rgba(69, 148, 249, 0.12);
        }
        .layer-source-icon.source-pen {
          color: #4594f9;
          background: rgba(69, 148, 249, 0.12);
        }
        .layer-name {
          color: white;
          font-size: 13px;
          font-family: Avenir, sans-serif;
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.4;
        }
        .theme-light .layer-name { color: #333; }
        .layer-rename-input {
          background-color: #414141;
          border: 1px solid #4594f9;
          border-radius: 4px;
          color: white;
          font-size: 13px;
          padding: 6px 10px;
          outline: none;
          width: 100%;
          font-family: Avenir, sans-serif;
          box-sizing: border-box;
        }
        .layer-rename-input::placeholder {
          color: #888;
          font-size: 12px;
        }
        .layer-actions {
          display: flex;
          gap: 6px;
          justify-content: center;
          width: 100%;
        }
        .layer-action-btn {
          width: 30px;
          height: 30px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #555;
          color: white;
          transition: background 0.2s ease, color 0.2s ease;
          flex-shrink: 0;
        }

        .theme-dark .side-menu-button svg,
        .theme-dark .bottom-menu-button svg {
          color: white;
        }
        .theme-light .side-menu-button svg,
        .theme-light .bottom-menu-button svg {
          color: #333;
        }
        .theme-dark .side-menu-button.selected svg,
        .theme-dark .bottom-menu-button.selected svg,
        .theme-dark .side-menu-button.active-tool svg,
        .theme-dark .bottom-menu-button.active-tool svg {
          color: white;
        }
        .theme-light .side-menu-button.selected svg,
        .theme-light .bottom-menu-button.selected svg,
        .theme-light .side-menu-button.active-tool svg,
        .theme-light .bottom-menu-button.active-tool svg {
          color: #333;
        }

        .theme-light .layer-action-btn { background: #e0e0e0; color: #666; }
        .layer-action-btn:hover:not(.disabled) { background: #4594f9; color: white; }
        .layer-action-btn.delete-btn:hover { background: #e02f1d; color: white; }
        .layer-action-btn.lock-btn:hover { background: #ffa500; color: white; }
        .layer-action-btn.disabled { opacity: 0.3; cursor: not-allowed; }

        /* ============================================
           ROOMS TAB
           ============================================ */
        .rooms-panel {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
        }

        /* -------- Add Room section -------- */
        .rooms-add-section {
          flex-shrink: 0;
          padding: 14px 16px 10px 16px;
          border-bottom: 1px solid #444;
          background: #333;
        }
        .theme-light .rooms-add-section {
          background: #fafafa;
          border-bottom-color: #e8e8e8;
        }

        .rooms-add-btn {
          width: 100%;
          padding: 11px 12px;
          border-radius: 10px;
          border: 1.5px dashed #555;
          background: transparent;
          color: #aaa;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: Avenir, sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
          outline: none;
        }
        .theme-light .rooms-add-btn {
          border-color: #ccc;
          color: #777;
        }
        .rooms-add-btn:hover:not(:disabled) {
          border-color: #4594f9;
          color: #4594f9;
          background: rgba(69, 148, 249, 0.06);
        }
        .rooms-add-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .rooms-add-spinner {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid rgba(69, 148, 249, 0.25);
          border-top-color: #4594f9;
          animation: roomsSpin 0.7s linear infinite;
          display: inline-block;
        }
        @keyframes roomsSpin {
          to { transform: rotate(360deg); }
        }
        .rooms-add-hint {
          margin-top: 8px;
          font-size: 10.5px;
          font-family: Avenir, sans-serif;
          color: #777;
          line-height: 1.4;
          text-align: center;
        }
        .theme-light .rooms-add-hint { color: #999; }
        .theme-dark .rooms-add-hint { color: #888; }

        /* -------- Rooms empty state -------- */
        .rooms-empty {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 24px;
          text-align: center;
          color: #888;
          font-family: Avenir, sans-serif;
          gap: 8px;
        }
        .rooms-empty svg {
          color: #4594f9;
          opacity: 0.6;
          margin-bottom: 4px;
        }
        .rooms-empty-title {
          margin: 0;
          font-size: 13px;
          font-weight: 600;
          color: #bbb;
        }
        .theme-light .rooms-empty-title { color: #555; }
        .rooms-empty-hint {
          margin: 0;
          font-size: 11px;
          line-height: 1.5;
          max-width: 240px;
          color: #777;
        }
        .theme-light .rooms-empty-hint { color: #999; }

        /* -------- Room list -------- */
        .rooms-list {
          flex: 1 1 auto;
          overflow-y: auto;
          min-height: 0;
          padding: 8px 0 12px 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .room-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          margin: 0 12px;
          padding: 12px;
          min-height: 112px;
          border-radius: 14px;
          background: #3a3a3a;
          border: 1.5px solid transparent;
          cursor: pointer;
          transition: background 0.18s ease, border-color 0.18s ease;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          outline: none;
          overflow: hidden;
          box-sizing: border-box;
        }
        .theme-light .room-item {
          background: #f5f5f5;
        }
        .room-item:hover {
          background: #444;
          border-color: rgba(69, 148, 249, 0.25);
        }
        .theme-light .room-item:hover {
          background: #ededed;
          border-color: rgba(69, 148, 249, 0.3);
        }

        /* --------------------------------------------------------------
           ACTIVE ROOM
           Subtle lighter gray in dark mode, subtle darker gray in light
           mode. No translucent blue, no glow, no shadow.
           -------------------------------------------------------------- */
        .room-item.active {
          background: #4a4a4a;
          border-color: #666;
        }
        .theme-light .room-item.active {
          background: #e8e8e8;
          border-color: #d0d0d0;
        }
        .room-item.editing {
          border-color: #4594f9;
        }

        .room-item.primary-room {
          background: #383838;
        }
        .theme-light .room-item.primary-room {
          background: #f0f0f0;
        }
        .room-item.primary-room.active {
          background: #4a4a4a;
        }
        .theme-light .room-item.primary-room.active {
          background: #e8e8e8;
        }

        /* Thumbnail — 2px border at all times so switching to the active
           gray border causes zero layout shift. No box-shadow, no glow,
           no ring. */
        .room-thumbnail {
          position: relative;
          width: 88px;
          height: 88px;
          flex-shrink: 0;
          border-radius: 10px;
          overflow: hidden;
          background: #2c2c2c;
          border: 2px solid #444;
          box-sizing: border-box;
          transition: border-color 0.18s ease;
        }
        .theme-light .room-thumbnail {
          background: #ffffff;
          border-color: #e0e0e0;
        }
        /* Active state — blue border on the thumbnail. This is now the
           sole active-state indicator on the thumbnail itself (the
           checkmark badge has been removed). The border is 2px at all
           times, so switching from gray to blue causes zero layout
           shift. */
        .room-item.active .room-thumbnail {
          border-color: #4594f9;
        }
        .theme-light .room-item.active .room-thumbnail {
          border-color: #4594f9;
        }
        .room-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.25s ease;
          user-select: none;
          -webkit-user-drag: none;
        }
        .room-item:hover .room-thumbnail img {
          transform: scale(1.04);
        }
        .room-thumbnail-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
          background: #2c2c2c;
        }
        .theme-light .room-thumbnail-fallback {
          color: #aaa;
          background: #f0f0f0;
        }

        /* --------------------------------------------------------------
           NUMBER BADGE — top-left of the thumbnail.
           Solid #4594f9 circle, solid #ffffff number, thin white ring so
           it stays legible over any thumbnail image. No glow, no shadow.
           -------------------------------------------------------------- */
        .room-thumbnail-number {
          position: absolute;
          top: 5px;
          left: 5px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #888888;
          color: #ffffff;
          font-family: Avenir, sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1.5px solid #bbbbbb;
          box-sizing: border-box;
          line-height: 1;
          z-index: 2;
          transition: background 0.18s ease;
        }
        .room-thumbnail-number-primary {
          cursor: default;
        }
        .room-thumbnail-number-button {
          cursor: pointer;
        }
        .room-thumbnail-number-button:hover {
          background: #3a7ed4;
        }
        .room-thumbnail-number-button:focus-visible {
          outline: 2px solid #4594f9;
          outline-offset: 2px;
        }

        .room-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .room-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .room-name {
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          font-family: Avenir, sans-serif;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.3;
          min-width: 0;
          transition: color 0.18s ease;
        }
        .theme-light .room-name { color: #222; }
        .room-item.active .room-name { color: #fff; }
        .theme-light .room-item.active .room-name { color: #333; }

        /* Sublabel — gray text, project name in bold so it pops. */
        .room-sublabel {
          color: #888;
          font-size: 10.5px;
          font-family: Avenir, sans-serif;
          letter-spacing: 0.1px;
          line-height: 1.4;
          display: block;
          white-space: normal;
          transition: color 0.18s ease;
        }
        .theme-light .room-sublabel { color: #888; }
        .room-item.active .room-sublabel { color: #999; }
        .theme-light .room-item.active .room-sublabel { color: #888; }

        .room-sublabel-project {
          font-weight: 700;
          color: #cfcfcf;
        }
        .theme-light .room-sublabel-project { color: #555; }
        .room-item.active .room-sublabel-project { color: #e0e0e0; }
        .theme-light .room-item.active .room-sublabel-project { color: #555; }

        .room-meta {
          color: #888;
          font-size: 11.5px;
          font-family: Avenir, sans-serif;
          letter-spacing: 0.2px;
          transition: color 0.18s ease;
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .theme-light .room-meta { color: #888; }
        .room-item.active .room-meta { color: #aaa; }
        .theme-light .room-item.active .room-meta { color: #888; }

        /* Layer count — sits above the status label. Inherits the gray
           tone of .room-meta in every theme/active state, so it stays
           steady while only the status label below it animates. */
        .room-layer-count {
          white-space: nowrap;
        }

        /* --------------------------------------------------------------
           "Tap to Toggle" affordance hint.
           Slow, smooth gray → blue → gray color cycle at 100% opacity —
           no fading. The layer-count suffix sits outside the span and
           stays steady gray, so the color shift reads as a single
           call-to-action rather than visual noise. 3.5 s ease-in-out is
           slow enough to feel calm and fast enough to catch the eye on
           first scan of the room list.

           The gray end of the cycle is driven by --room-meta-base so it
           matches the surrounding meta text in every theme/active state
           instead of fighting it. Only the blue peak is a fixed color.
           -------------------------------------------------------------- */
        .room-meta-toggle-hint {
          --room-meta-base: #888;
          display: inline-block;
          color: var(--room-meta-base);
          animation: roomMetaTogglePulse 3.5s ease-in-out infinite;
          will-change: color;
        }
        .room-item.active .room-meta-toggle-hint {
          --room-meta-base: #aaa;
        }
        .theme-light .room-item.active .room-meta-toggle-hint {
          --room-meta-base: #888;
        }

        @keyframes roomMetaTogglePulse {
          0%,
          100% { color: var(--room-meta-base); }
          50%  { color: #4594f9; }
        }

        /* --------------------------------------------------------------
           "Active" status label — shown on the currently-selected room
           in place of the "Tap to Toggle" hint. Solid blue, held steady
           at 100% opacity: no animation, no fade. Mirrors the blue peak
           of the adjacent pulse so the two states read as a matched
           pair when a room flips between inactive and active.
           -------------------------------------------------------------- */
        .room-meta-active {
          color: #4594f9;
          font-weight: 600;
        }

        /* Respect reduced-motion preference — fall back to a static gray
           label rather than a color cycle for users who opted out of
           motion. */
        @media (prefers-reduced-motion: reduce) {
          .room-meta-toggle-hint {
            animation: none;
            color: var(--room-meta-base);
          }
        }

        .room-rename-input {
          background: #414141;
          border: 1px solid #4594f9;
          border-radius: 5px;
          color: #fff;
          font-size: 13px;
          font-family: Avenir, sans-serif;
          padding: 5px 8px;
          outline: none;
          width: 100%;
          box-sizing: border-box;
        }
        .theme-light .room-rename-input {
          background: #ffffff;
          color: #333;
        }

        .room-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
          position: relative;
          z-index: 2;
        }
        .room-action-btn {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          border: none;
          background: rgba(255, 255, 255, 0.06);
          color: #aaa;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.18s ease, color 0.18s ease;
          outline: none;
        }
        .theme-light .room-action-btn {
          background: rgba(0, 0, 0, 0.05);
          color: #777;
        }
        .room-item.active .room-action-btn {
          background: rgba(255, 255, 255, 0.08);
          color: #ccc;
        }
        .theme-light .room-item.active .room-action-btn {
          background: rgba(0, 0, 0, 0.05);
          color: #777;
        }
        .room-action-btn:hover {
          background: #4594f9;
          color: #fff;
        }
        .room-delete-btn:hover {
          background: #e02f1d;
          color: #fff;
        }

        /* Mobile tuning */
        @media (max-width: 767px) {
          .rooms-list { padding: 8px 0 16px 0; }
          .room-item { margin: 0 10px; padding: 10px; min-height: 100px; gap: 12px; }
          .room-thumbnail { width: 76px; height: 76px; }
          .room-name { font-size: 14px; }
          .room-sublabel { font-size: 10px; }
          .room-meta { font-size: 11px; }
          .room-thumbnail-number {
            width: 20px;
            height: 20px;
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default LayersComponent;
export { LayersComponent };